import { useContext, useEffect, useMemo, useState } from "react";
import { SidebarContext } from "../../context/SidebarContext.js";
import SectionMenuLeft from "../../components/SectionMenuLeft/SectionMenuLeft.jsx";
import "./Notifications.css";
import { toast, ToastContainer } from "react-toastify";
import { config } from "../../config.js";
import ReactTable from "../../components/ReactTable/ReactTable.jsx";
import { LuArrowLeft } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

/* ---------------- TABLE SKELETON ---------------- */
const TableSkeleton = ({ rows = 6 }) => (
  <div className="table-skeleton">
    {[...Array(rows)].map((_, i) => (
      <div className="skeleton-row" key={i}>
        <div className="skeleton-cell" />
        <div className="skeleton-cell" />
        <div className="skeleton-cell" />
        <div className="skeleton-cell" />
        <div className="skeleton-cell" />
      </div>
    ))}
  </div>
);
const Notifications = () => {
  const { isSidebar } = useContext(SidebarContext);
  const role = localStorage.getItem("adminRole");
  const adminCity =
    role === "sub-admin" ? localStorage.getItem("adminCity") : null;
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    body: "",
    sentTo: "users",
  });
  const [imageFile, setImageFile] = useState(null);
  const MAX_FILE_SIZE = 300 * 1024; // 300 KB

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    //  Allowed types
    const allowedTypes = ["image/jpeg", "image/png"];

    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG and PNG files are allowed");
      e.target.value = null;
      return;
    }

    //  Size check
    if (file.size > MAX_FILE_SIZE) {
      toast.error("Image must be less than 300 KB");
      e.target.value = null;
      return;
    }

    //  Valid file
    setImageFile(file);
  };
  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);

      const response = await fetch(
        `${config.backEndBaseUrl}notification/${deleteId}`,
        {
          method: "DELETE",
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "Failed to delete notification");
      }

      toast.success("Notification deleted successfully");

      setShowDeleteModal(false);
      setDeleteId(null);

      fetchNotifications();
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setDeleting(false);
    }
  };

  const handleSendNotification = async () => {
    if (!formData.title.trim() || !formData.body.trim()) {
      toast.error("Title and Body are required");
      return;
    }

    try {
      setLoading(true);

      const payload = new FormData();
      payload.append("title", formData.title);
      payload.append("body", formData.body);
      payload.append("sentTo", formData.sentTo);

      if (imageFile) {
        payload.append("image", imageFile);
      }

      const response = await fetch(
        `${config.backEndBaseUrl}notification/custom/send${adminCity ? `?adc=${adminCity}` : ""}`,
        {
          method: "POST",
          body: payload,
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Something went wrong");
      }

      setResult({
        totalSent: data.totalSent,
        totalFailed: data.totalFailed,
        totalSkipped: data.totalSkipped,
      });

      toast.success("Notification sent successfully");
      setShowModal(false);

      setFormData({
        title: "",
        body: "",
        sentTo: "users",
      });

      setImageFile(null);
    } catch (error) {
      toast.error(error.message || "Failed to send notification");
    } finally {
      setLoading(false);
      fetchNotifications();
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoadingList(true);

      const response = await fetch(
        `${config.backEndBaseUrl}notification/custom/list`,
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Failed to fetch notifications");
      }

      setNotifications(data.data || []);
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setLoadingList(false);
    }
  };
  useEffect(() => {
    fetchNotifications();
  }, []);

  const columns = useMemo(
    () => [
      {
        header: "Title",
        accessorKey: "title",
      },
      {
        header: "Message",
        accessorKey: "body",
      },
      {
        header: "Sent To",
        accessorKey: "sentTo",
        Cell: ({ cell }) => (
          <span
            className={`badge ${cell.row.original.sentTo == "users" ? "bg-dark" : "bg-primary"}  text-uppercase`}
          >
            {cell.row.original.sentTo}
          </span>
        ),
      },
      {
        header: "Delivered",
        accessorKey: "totalSent",
        Cell: ({ cell }) => (
          <span className="text-success fw-bold">
            {cell.row.original.totalSent}
          </span>
        ),
      },
      {
        header: "Failed",
        accessorKey: "totalFailed",
        Cell: ({ cell }) => (
          <span className="text-danger fw-bold">
            {cell.row.original.totalFailed}
          </span>
        ),
      },
      {
        header: "Token Skipped",
        accessorKey: "totalSkipped",
        Cell: ({ cell }) => (
          <span className="text-primary fw-bold">
            {cell.row.original.totalSkipped}
          </span>
        ),
      },
      {
        header: "Sent At",
        accessorKey: "sentAt",
        Cell: ({ cell }) => {
          const value = cell.row.original.sentAt._seconds;

          if (!value) return "-";

          const date = value?.seconds
            ? new Date(value.seconds * 1000)
            : new Date(value);

          return date.toLocaleString();
        },
      },
      {
        header: "Delete",
        accessorKey: "id",
        Cell: ({ cell }) => (
          <button
            className="btn btn-danger"
            onClick={() => {
              setDeleteId(cell.row.original.id);
              setShowDeleteModal(true);
            }}
          >
            Delete
          </button>
        ),
      },
    ],
    [],
  );

  return (
    <div className="body">
      <div id="wrapper">
        <div id="page">
          <div className="layout-wrap">
            <SectionMenuLeft />

            <div className="section-content-right">
              <div
                className="main-content"
                style={{ paddingLeft: isSidebar ? "280px" : 0 }}
              >
                <div className="main-content-inner">
                  <ToastContainer />

                  <div className="wg-box">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <div className="flex items-center justify-between mb-4 gap-3">
                        <div className="flex items-center gap-3">
                          <button
                            className="back-btn flex items-center gap-2"
                            onClick={() => navigate("/")}
                          >
                            <LuArrowLeft size={16} />
                            {/* <span>Back</span> */}
                          </button>

                          <h5 className="fare-heading">Notifications</h5>
                        </div>
                      </div>
                      <button
                        className="btn btn-success custom-open-btn"
                        onClick={() => setShowModal(true)}
                      >
                        Send Notification
                      </button>
                    </div>

                    {result && (
                      <div className="notification-alert alert alert-success alert-dismissible fade show mt-3">
                        <div className="d-flex justify-content-between align-items-center flex-wrap">
                          <div>
                            <strong>Bulk Notification Completed</strong>
                            <div className="mt-1 small">
                              Delivered: <strong>{result.totalSent}</strong> |
                              Failed:{" "}
                              <strong className="text-danger">
                                {" "}
                                {result.totalFailed}
                              </strong>{" "}
                              | No Token:{" "}
                              <strong className="text-primary">
                                {" "}
                                {result.totalSkipped}
                              </strong>
                            </div>
                          </div>

                          <button
                            type="button"
                            className="btn-close"
                            onClick={() => setResult(null)}
                          ></button>
                        </div>
                      </div>
                    )}

                    {loadingList ? (
                      <TableSkeleton />
                    ) : (
                      <ReactTable columns={columns} data={notifications} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <>
          <div
            className="custom-modal-noti modal fade show d-block"
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content custom-modal-content">
                <div className="modal-header custom-modal-header">
                  <h5 className="modal-title">Send Notification</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  ></button>
                </div>

                <div className="modal-body">
                  <div className="mb-4">
                    <label className="form-label custom-label">Title</label>
                    <input
                      type="text"
                      className="form-control custom-input"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Enter notification title"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="form-label custom-label">Body</label>
                    <textarea
                      className="form-control custom-input"
                      rows="4"
                      name="body"
                      value={formData.body}
                      onChange={handleChange}
                      placeholder="Enter notification message"
                    ></textarea>
                  </div>
                  <div className="mb-4">
                    <label className="form-label custom-label">
                      Notification Image (Optional)
                    </label>

                    <input
                      type="file"
                      className="form-control custom-input"
                      accept="image/*"
                      onChange={handleImageChange}
                    />

                    <small className="text-muted">Supported: JPG, PNG</small>
                    {imageFile && (
                      <img
                        src={URL.createObjectURL(imageFile)}
                        style={{
                          width: "100%",
                          borderRadius: "6px",
                          marginTop: "10px",
                        }}
                      />
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label custom-label">Send To</label>
                    <select
                      className="form-select custom-input"
                      name="sentTo"
                      value={formData.sentTo}
                      onChange={handleChange}
                    >
                      <option value="users">All Users</option>
                      <option value="drivers">All Drivers</option>
                    </select>
                  </div>
                </div>

                <div className="modal-footer border-0 pt-0">
                  <button
                    className="btn btn-dark"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn custom-send-btn"
                    onClick={handleSendNotification}
                    disabled={loading}
                  >
                    {loading ? "Sending..." : "Send Notification"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show custom-backdrop"></div>
        </>
      )}
      {showDeleteModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Delete Notification</h5>
                  <button
                    className="btn-close"
                    onClick={() => setShowDeleteModal(false)}
                  ></button>
                </div>

                <div className="modal-body text-center">
                  <p className="mb-0">
                    Are you sure you want to delete this notification?
                  </p>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-dark"
                    onClick={() => setShowDeleteModal(false)}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-danger"
                    onClick={confirmDelete}
                    disabled={deleting}
                  >
                    {deleting ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default Notifications;
